import { useState, useCallback, useRef } from 'react'

/**
 * Scroll behavior events that can occur in the chat interface
 */
export const ScrollEvent = {
  USER_SUBMITTED: 'user_submitted',
  GENERATION_STARTED: 'generation_started',
  GENERATION_UPDATED: 'generation_updated',
  GENERATION_FINISHED: 'generation_finished',
  CONVERSATION_LOADED: 'conversation_loaded'
}

/**
 * Scroll actions that can be taken in response to events
 */
export const ScrollAction = {
  SCROLL_TO_BOTTOM: 'scroll_to_bottom',
  SCROLL_TO_MESSAGE_TOP: 'scroll_to_message_top',
  NONE: 'none'
}

/**
 * Determines the appropriate scroll action for a given event and attention state
 *
 * Event → Action Matrix:
 * | Event                | Paying Attention      | Scrolled Away |
 * |----------------------|-----------------------|---------------|
 * | User submitted       | scroll to bottom      | scroll to bottom |
 * | Generation started   | scroll to message top | none |
 * | Message updated      | none                  | none |
 * | Generation finished  | scroll to message top | none |
 * | Conversation loaded  | scroll to message top | N/A |
 */
export function getScrollAction (event, userIsAttentive) {
  switch (event) {
    case ScrollEvent.USER_SUBMITTED:
      // Always scroll to bottom when user submits - they need to see their message
      return ScrollAction.SCROLL_TO_BOTTOM

    case ScrollEvent.GENERATION_STARTED:
      // Scroll to message top to prepare for reading, but only if user is attentive
      return userIsAttentive
        ? ScrollAction.SCROLL_TO_MESSAGE_TOP
        : ScrollAction.NONE

    case ScrollEvent.GENERATION_UPDATED:
      // Do nothing during updates - let user read naturally
      return ScrollAction.NONE

    case ScrollEvent.GENERATION_FINISHED:
      // Final scroll to message top if user stuck around
      return userIsAttentive
        ? ScrollAction.SCROLL_TO_MESSAGE_TOP
        : ScrollAction.NONE

    case ScrollEvent.CONVERSATION_LOADED:
      // Scroll to message top when loading a conversation
      return ScrollAction.SCROLL_TO_MESSAGE_TOP

    default:
      return ScrollAction.NONE
  }
}

/**
 * Hook to manage scroll behavior in the chat interface
 *
 * @param {React.RefObject} wrapperRef - Ref to the scrollable container
 * @param {Object} options - Configuration options
 * @param {number} options.messageTopPadding - Padding above message when scrolling to top (default: 24)
 * @returns {Object} Scroll behavior controls
 */
export function useScrollBehavior (wrapperRef, options = {}) {
  const { messageTopPadding = 24 } = options

  // Track whether user is "paying attention" (hasn't scrolled away during generation)
  const [userIsAttentive, setUserIsAttentive] = useState(true)

  // Track whether we're currently auto-scrolling (to ignore our own scroll events)
  const isAutoScrollingRef = useRef(false)

  /**
   * Execute a scroll action
   */
  const executeAction = useCallback((action) => {
    const wrapper = wrapperRef.current
    if (!wrapper || action === ScrollAction.NONE) return

    isAutoScrollingRef.current = true

    switch (action) {
      case ScrollAction.SCROLL_TO_BOTTOM:
        wrapper.scrollTop = wrapper.scrollHeight + 1000
        // Double-check after a brief delay for any late content
        setTimeout(() => {
          if (wrapperRef.current) {
            wrapperRef.current.scrollTop = wrapperRef.current.scrollHeight + 1000
          }
        }, 50)
        break

      case ScrollAction.SCROLL_TO_MESSAGE_TOP: {
        const lastMessage = wrapper.querySelector('[data-thread-message-id]:last-of-type')
        if (lastMessage) {
          wrapper.scrollTop = lastMessage.offsetTop - messageTopPadding
        }
        break
      }

      default:
        break
    }

    // Clear the auto-scrolling flag after smooth scroll animation completes
    // Use a longer delay to account for smooth scrolling (scrollBehavior: 'smooth')
    setTimeout(() => {
      isAutoScrollingRef.current = false
    }, 500)
  }, [wrapperRef, messageTopPadding])

  /**
   * Handle a scroll event - determines and executes the appropriate action
   */
  const handleEvent = useCallback((event) => {
    const action = getScrollAction(event, userIsAttentive)
    executeAction(action)
  }, [userIsAttentive, executeAction])

  /**
   * Handle user scroll during generation - marks user as "not attentive" if they scroll away
   * Should be called from the scroll event handler
   */
  const handleUserScroll = useCallback((isGenerating) => {
    // Ignore scroll events triggered by our auto-scroll
    if (isAutoScrollingRef.current) return

    // If user scrolls during generation, check if they've actually scrolled away
    // from the message top (not just minor scroll adjustments from content changes)
    if (isGenerating) {
      const wrapper = wrapperRef.current
      if (!wrapper) return

      const lastMessage = wrapper.querySelector('[data-thread-message-id]:last-of-type')
      if (lastMessage) {
        const expectedScrollTop = lastMessage.offsetTop - messageTopPadding
        const currentScrollTop = wrapper.scrollTop
        const scrollDifference = Math.abs(currentScrollTop - expectedScrollTop)

        // Only mark as "scrolled away" if user scrolled more than 100px from expected position
        if (scrollDifference > 100) {
          setUserIsAttentive(false)
        }
      }
    }
  }, [wrapperRef, messageTopPadding])

  /**
   * Reset attention state - call when a new generation starts
   */
  const resetAttention = useCallback(() => {
    setUserIsAttentive(true)
  }, [])

  /**
   * Check if scroll container is at/near the bottom
   */
  const checkIsAtBottom = useCallback((threshold = 50) => {
    const wrapper = wrapperRef.current
    if (!wrapper) return true

    return wrapper.scrollHeight - wrapper.scrollTop - wrapper.clientHeight < threshold
  }, [wrapperRef])

  return {
    handleEvent,
    handleUserScroll,
    resetAttention,
    checkIsAtBottom,
    userIsAttentive,
    isAutoScrollingRef
  }
}
